/**
 * ENG-4897 — pure decision helper for `.mcp.json` drift detection.
 *
 * Extracted from manager-worker.ts so unit tests can exercise the
 * decision tree without importing the entire worker (which has heavy
 * side effects at module load).
 *
 * The manager polls every 10s. After it deploys the latest `.mcp.json`
 * to a persistent claude session's project directory, it asks: has the
 * file changed since the running claude was launched? Three outcomes
 * matter:
 *   - No file on disk yet → nothing to compare.
 *   - First observation (no recorded baseline) → adopt current as baseline.
 *   - Mismatch → schedule a tmux respawn so claude reads the fresh config.
 *
 * The optimistic baseline (we adopt whatever's on disk on first poll
 * after manager start) is the trade-off documented in the issue. Worst
 * case is one missed drift event after a manager restart; the next
 * change is caught.
 */

import { createHash } from 'node:crypto';

export type McpDriftAction =
  | { kind: 'no-config' }
  | { kind: 'baseline'; hash: string }
  | { kind: 'no-drift' }
  | { kind: 'drift'; previous: string; current: string };

export function decideMcpDriftAction(
  currentHash: string | null,
  knownHash: string | undefined,
): McpDriftAction {
  if (!currentHash) return { kind: 'no-config' };
  if (!knownHash) return { kind: 'baseline', hash: currentHash };
  if (knownHash === currentHash) return { kind: 'no-drift' };
  return { kind: 'drift', previous: knownHash, current: currentHash };
}

/**
 * ENG-5537 — given a content `drift` was detected, decide whether it warrants
 * restarting the running claude session.
 *
 * Only a *membership* change to the declared MCP server set (a server added or
 * removed) requires a restart: the running session connected to a fixed set of
 * servers at launch, and gaining/losing one means the live session's tool
 * surface is wrong until it respawns. A *value-only* drift — the same set of
 * servers with a rotated url or header value, e.g. Composio re-signing its
 * `mcp_url` on most polls — leaves the running session's established
 * connections intact and is picked up by the next natural respawn, so it must
 * NOT restart. Restarting on value-only drift is exactly what flapped agents
 * into the ENG-5441 circuit breaker (phil, agt-aws-1, 2026-05-25).
 *
 * When either key set is unknown (file missing / unparseable on one side), we
 * keep the pre-ENG-5537 safe default: treat it as restart-worthy.
 */
export interface McpDriftRestartDecision {
  /** Restart the running session? True only on membership change or unknown. */
  restart: boolean;
  /** Key membership could not be determined (null/undefined on either side). */
  membershipUnknown: boolean;
  /** Server keys added or removed (sorted; empty for value-only or unknown). */
  addedOrRemoved: string[];
}

export function decideMcpRestartOnDrift(
  previousKeys: Set<string> | null | undefined,
  currentKeys: Set<string> | null | undefined,
): McpDriftRestartDecision {
  if (!previousKeys || !currentKeys) {
    return { restart: true, membershipUnknown: true, addedOrRemoved: [] };
  }
  const addedOrRemoved: string[] = [];
  for (const k of currentKeys) if (!previousKeys.has(k)) addedOrRemoved.push(k);
  for (const k of previousKeys) if (!currentKeys.has(k)) addedOrRemoved.push(k);
  addedOrRemoved.sort();
  return { restart: addedOrRemoved.length > 0, membershipUnknown: false, addedOrRemoved };
}

/**
 * ENG-5537 — structure-only hash of a managed-toolkit desired set, used to gate
 * the convergence-driven session restart independently of the value hash that
 * gates the `.mcp.json` write.
 *
 * Deliberately hashes only each server's id and the SORTED NAMES of its header
 * keys — never the url or header values. A provider rotating a signed `mcp_url`
 * (or a credential-bearing header value) moves the value hash, so `.mcp.json`
 * re-converges to the live credential, but leaves THIS hash unchanged, so the
 * running session is left alone. Adding/removing a toolkit, or a header key
 * appearing/disappearing, does move this hash and triggers the restart.
 */
export function managedMcpStructureHash(
  entries: Array<{ serverId: string; headers?: Record<string, string> }>,
): string {
  const basis = entries
    .slice()
    .sort((a, b) => a.serverId.localeCompare(b.serverId))
    .map((e) => `${e.serverId}|${Object.keys(e.headers ?? {}).sort().join(',')}`)
    .join('\n');
  return createHash('sha256').update(basis).digest('hex').slice(0, 16);
}

/**
 * ENG-6062 — VALUE hash of the channel-owned secrets in `.env.integrations`.
 *
 * The deliberate counterpart to {@link managedMcpStructureHash}: that hash is
 * value-BLIND (ENG-5537) so a Composio `mcp_url` / header re-sign on most polls
 * doesn't flap the session into the ENG-5441 breaker. But after ENG-5901
 * Track D, channel tokens (SLACK_BOT_TOKEN, TELEGRAM_BOT_TOKEN,
 * MSTEAMS_CLIENT_SECRET, …) are `${VAR}` templates in `.mcp.json` with the raw
 * value in `.env.integrations`. A token rotation changes only that value —
 * `.mcp.json` stays byte-identical — so neither the `.mcp.json` content hash nor
 * the structure hash moves, and the running channel MCP keeps the old, now
 * provider-revoked token until something else restarts the session (observed:
 * Vigil's Telegram outage, agt-aws-1, 2026-06-05).
 *
 * This hash IS value-sensitive, but scoped to the fixed
 * `channelSecretKeys` set — those rotate only on a deliberate operator action,
 * never per-poll, so reacting to them cannot reintroduce the ENG-5441 flap
 * (writeChannelCredentials upserts the same value idempotently between
 * rotations → stable hash). Pass `CHANNEL_SECRET_ENV_KEYS` from
 * `@augmented/core` as the key set so the list stays single-sourced with the
 * writers.
 *
 * Returns null when none of the keys are present (no channel secrets to track →
 * no drift signal, handled as `no-config` by {@link decideMcpDriftAction}).
 */
export function channelSecretValueHash(
  envEntries: Record<string, string> | null | undefined,
  channelSecretKeys: readonly string[],
): string | null {
  if (!envEntries) return null;
  const basis = channelSecretKeys
    .slice()
    .sort()
    .filter((k) => Object.prototype.hasOwnProperty.call(envEntries, k))
    .map((k) => `${k}=${envEntries[k]}`)
    .join('\n');
  if (basis.length === 0) return null;
  return createHash('sha256').update(basis).digest('hex').slice(0, 16);
}
